import React, { useState, useEffect } from 'react';
import ProductGrid from '../components/ProductGrid';
import { productService } from '../services/product';
import categoryService from '../services/category';
import { Product } from '../types';
import { useCart } from '../context/CartContext';

const ProductsPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const { addItemToCart } = useCart();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await categoryService.getAll();
        setCategories(data || []);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const params: any = {};
        
        if (searchTerm) params.search = searchTerm;
        if (selectedCategory) params.categories__slug = selectedCategory;
        if (priceRange.min) params.min_price = parseFloat(priceRange.min);
        if (priceRange.max) params.max_price = parseFloat(priceRange.max);
        
        const response = await productService.getAll(params);
        setProducts(response.results || []);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [searchTerm, selectedCategory, priceRange]);

  const handleAddToCart = async (productId: number) => {
    try {
      // Selección rápida de sede
      const input = window.prompt('Selecciona sede: escribe SANTIAGO o ANTOFAGASTA');
      const loc = (input || '').trim().toUpperCase();
      if (loc !== 'SANTIAGO' && loc !== 'ANTOFAGASTA') {
        alert('Debes ingresar "SANTIAGO" o "ANTOFAGASTA"');
        return;
      }
      await addItemToCart(productId.toString(), 1, loc);
      console.log('Producto agregado al carrito:', productId, 'ubicación:', loc);
    } catch (error) {
      console.error('Error al agregar al carrito:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-[96px]">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* Sidebar de Filtros (Izquierda) */}
          <aside className="w-full lg:w-64 flex-shrink-0 space-y-8">
            {/* Categorías */}
            <div>
              <h3 className="font-serif text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Categorías
              </h3>
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedCategory('')}
                  className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                    selectedCategory === '' 
                      ? 'bg-gray-900 text-white font-medium' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Todas las categorías
                </button>
                {categories.map((category) => (
                  <div key={category.id} className="space-y-1">
                    <button
                      onClick={() => setSelectedCategory(category.slug)}
                      className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                        selectedCategory === category.slug 
                          ? 'bg-gray-900 text-white font-medium' 
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {category.name}
                    </button>
                    {/* Subcategorías indentadas */}
                    {category.subcategories && category.subcategories.map((sub: any) => (
                      <button
                        key={sub.id}
                        onClick={() => setSelectedCategory(sub.slug)}
                        className={`w-full text-left pl-6 pr-3 py-1.5 text-sm rounded-md transition-colors ${
                          selectedCategory === sub.slug 
                            ? 'text-primary-accent font-medium bg-pink-50' 
                            : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        {sub.name}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Filtros Adicionales */}
            <div>
              <h3 className="font-serif text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Filtros
              </h3>
              
              {/* Búsqueda */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Buscar</label>
                <input
                  type="text"
                  placeholder="Nombre del producto..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm"
                />
              </div>

              {/* Rango de Precio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Precio</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    placeholder="Mín"
                    value={priceRange.min}
                    onChange={(e) => setPriceRange({...priceRange, min: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="number"
                    placeholder="Máx"
                    value={priceRange.max}
                    onChange={(e) => setPriceRange({...priceRange, max: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm"
                  />
                </div>
              </div>
            </div>
          </aside>

          {/* Grid de Productos (Derecha) */}
          <div className="flex-1">
            <div className="mb-6 flex justify-between items-center">
              <h1 className="text-2xl font-serif font-bold text-gray-900">
                {selectedCategory 
                  ? categories.find(c => c.slug === selectedCategory)?.name || 
                    categories.flatMap(c => c.subcategories || []).find((s: any) => s.slug === selectedCategory)?.name || 
                    'Productos'
                  : 'Todos los Productos'}
              </h1>
              <span className="text-sm text-gray-500">
                {products.length} resultados
              </span>
            </div>

            <ProductGrid
              products={products}
              onAddToCart={handleAddToCart}
              loading={loading}
              title="" // Título ya manejado arriba
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProductsPage;